import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Signup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    organization: "",
    role: "Viewer",
    region: "",
    reason: "",
  });

  const roles = [
    { value: "Government Health Officer", label: "Government Health Officer" },
    { value: "Organization User", label: "Organization User (NGO)" },
    { value: "Admin", label: "System Administrator" },
    { value: "Viewer", label: "Read-Only Viewer" },
  ];

  const regions = [
    "Adamawa", "Centre", "East", "Far-North", "Littoral",
    "North", "North-West", "South", "South-West", "West",
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.organization || !formData.region) {
      return;
    }

    setSubmitting(true);

    const body = new URLSearchParams();
    body.append("fullName", formData.fullName);
    body.append("email", formData.email);
    body.append("organization", formData.organization);
    body.append("role", formData.role);
    body.append("region", formData.region);
    body.append("reason", formData.reason);

    try {
      await fetch("https://readdy.ai/api/form/d7vpbsd3mj78tu7voaq0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch {
      // Show success state regardless
    }

    setSubmitted(true);
    setSubmitting(false);
    setTimeout(() => {
      navigate("/login");
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex items-center justify-center px-4 py-8 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-teal/10 rounded-full blur-[160px]" />
        <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] bg-amber/5 rounded-full blur-[140px]" />
      </div>

      <div className="relative w-full max-w-lg">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 flex items-center justify-center">
            <img
              src="https://public.readdy.ai/ai/img_res/0a8677a0-78d8-4971-947c-fe12fe0ee52e.png"
              alt="MalariaGuard"
              className="w-10 h-10 object-contain rounded"
            />
          </div>
          <div>
            <h1 className="font-display font-bold text-lg tracking-tight text-slate-900 dark:text-white">MalariaGuard</h1>
            <p className="text-[10px] text-slate-400 font-mono tracking-wider uppercase">EpiSense AI</p>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 backdrop-blur-sm p-6 md:p-8 shadow-xl dark:shadow-2xl dark:shadow-black/20">
          {submitted ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-teal/10 border border-teal/20 mx-auto mb-4">
                <i className="ri-check-line text-2xl text-teal" />
              </div>
              <h2 className="text-lg font-display font-semibold text-slate-900 dark:text-white mb-2">{t("auth_request_submitted")}</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">{t("auth_request_submitted_desc")}</p>
              <p className="text-xs text-slate-400 dark:text-slate-500">{t("auth_redirecting")}</p>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-display font-semibold text-slate-900 dark:text-white mb-1">{t("auth_request_access_title")}</h2>
              <p className="text-xs text-slate-500 dark:text-slate-500 mb-6">{t("auth_request_desc")}</p>

              <form data-readdy-form id="request-access-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">{t("auth_full_name")}</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950/60 px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal/30 transition-colors"
                      placeholder="Dr. Jean-Pierre Fotso"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">{t("auth_email")}</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950/60 px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal/30 transition-colors"
                      placeholder="name@organization.org"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">{t("auth_organization")}</label>
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      required
                      className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950/60 px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal/30 transition-colors"
                      placeholder="Ministry of Public Health"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">{t("auth_requested_role")}</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                      className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950/60 px-3 py-2.5 text-sm text-slate-900 dark:text-white focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal/30 transition-colors"
                    >
                      {roles.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">{t("auth_region_district")}</label>
                  <select
                    name="region"
                    value={formData.region}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950/60 px-3 py-2.5 text-sm text-slate-900 dark:text-white focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal/30 transition-colors"
                  >
                    <option value="">{t("auth_select_region")}</option>
                    {regions.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                    {t("auth_reason_access")}
                    <span className="text-slate-400 dark:text-slate-600 ml-1">{t("auth_char_count", { count: formData.reason.length })}</span>
                  </label>
                  <textarea
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    maxLength={500}
                    rows={3}
                    required
                    className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950/60 px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal/30 transition-colors resize-none"
                    placeholder={t("auth_reason_placeholder")}
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-lg bg-teal text-slate-950 text-sm font-semibold py-2.5 hover:bg-teal-hover transition-colors whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <i className="ri-send-plane-line text-sm" />
                  {submitting ? t("auth_submitting") : t("auth_submit_request")}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <Link to="/" className="text-xs text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">
              &larr; {t("auth_back_home")}
            </Link>
            <Link to="/login" className="text-xs text-teal hover:text-teal-hover transition-colors font-medium">
              {t("auth_already_have_access")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}